import { HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { isEmbeddedBrowser, isSafariBrowser } from './browser-detection';
import { DeviceInfoService } from './device-info.service';
import { FILE_PREVIEW_LOADING_HTML, FILE_PREVIEW_LOADING_TITLE } from './file-preview-html';
import { NativeFileHandlerService } from './native-file-handler.service';
import { StackedMessagesService } from '@shared/stacked-messages/stacked-messages.service';

export type GetFileOptions = {
  forceDownload?: boolean;
  tryOpenOnMobile?: boolean;
  previewTargetKey?: string;
  allowDownloadFallback?: boolean;
};

export const OPENABLE_FILE_EXTENSIONS: readonly string[] = ['.pdf', '.jpeg', '.jpg', '.png', '.bmp'];
export const DOWNLOADABLE_FILE_EXTENSIONS: readonly string[] = ['.zip', '.rar', '.xlsx', '.xls', '.doc', '.docx'];

@Injectable({ providedIn: 'root' })
export class FileHandlerService {
  private readonly preparedPreviewTargets = new Map<string, Window | null>();
  private readonly deviceInfoService = inject(DeviceInfoService);
  private readonly nativeFileHandlerService = inject(NativeFileHandlerService);
  private readonly notification = inject(StackedMessagesService);
  private previewTargetCounter = 0;

  public readonly OPENABLE_EXTENSIONS = [...OPENABLE_FILE_EXTENSIONS];
  public readonly DOWNLOADABLE_EXTENSIONS = [...DOWNLOADABLE_FILE_EXTENSIONS];

  public canOpenInBrowser(fileExtension?: string | null, fileMimeType?: string | null): boolean {
    const normalizedExtension = this.normalizeExtension(fileExtension);
    const normalizedMimeType = (fileMimeType || '').toLowerCase();

    return (
      this.OPENABLE_EXTENSIONS.includes(normalizedExtension) ||
      normalizedMimeType === 'application/pdf' ||
      (normalizedMimeType.startsWith('image/') && normalizedMimeType !== 'image/svg+xml')
    );
  }

  public preparePreviewTarget(key: string): string {
    if (!key) {
      return '';
    }

    const preparedPreviewTargetKey = this.buildPreparedPreviewTargetKey(key);

    const previewWindow = window.open('', '_blank');

    if (previewWindow) {
      this.renderLoadingPreview(previewWindow);
    }

    this.preparedPreviewTargets.set(preparedPreviewTargetKey, previewWindow);

    return preparedPreviewTargetKey;
  }

  public getFile(file: HttpResponse<Blob>, options?: boolean | GetFileOptions): boolean {
    const normalizedOptions = this.normalizeGetFileOptions(options);
    const fileName = this.getFileName(file);
    const fileExtension = this.normalizeExtension(file.headers.get('File-Extension'));
    const fileMimeType = file.headers.get('Content-Type');
    const deviceInfo = this.deviceInfoService.getInfo();

    const isDesktop = deviceInfo.deviceTypeDetected === 'DESKTOP';
    const isOpenableInTab = this.canOpenInBrowser(fileExtension, fileMimeType);
    const shouldDownloadInsteadOpen = this.DOWNLOADABLE_EXTENSIONS.includes(fileExtension);
    const previewWindow = normalizedOptions.previewTargetKey
      ? this.consumePreparedPreviewTarget(normalizedOptions.previewTargetKey)
      : null;

    if (!file.body) {
      this.closePreviewWindow(previewWindow);
      return false;
    }

    const fileBody = file.body;
    const shouldTryOpeningInTab =
      isOpenableInTab &&
      normalizedOptions.forceDownload === false &&
      (isDesktop || normalizedOptions.tryOpenOnMobile === true);

    if (shouldTryOpeningInTab) {
      const didOpenInTab = this.openFileInTab({
        fileBody,
        fileMimeType,
        fileName,
        previewWindow,
        useDirectNavigation: this.shouldUseDirectNavigationPreview(fileExtension, fileMimeType),
      });

      if (didOpenInTab) {
        return true;
      }

      this.downloadFile(fileBody, fileName);
      return true;
    }

    this.closePreviewWindow(previewWindow);

    if (
      normalizedOptions.forceDownload ||
      shouldDownloadInsteadOpen ||
      deviceInfo.deviceTypeDetected !== 'DESKTOP' ||
      normalizedOptions.allowDownloadFallback
    ) {
      this.downloadFile(fileBody, fileName);
      return true;
    }

    if (!isOpenableInTab) {
      this.showUnsupportedTypeError();
      return false;
    }

    this.downloadFile(fileBody, fileName);
    return true;
  }

  public clearPreparedPreviewTarget(key: string): void {
    const previewWindow = this.preparedPreviewTargets.get(key);

    if (previewWindow && !previewWindow.closed) {
      previewWindow.close();
    }

    this.preparedPreviewTargets.delete(key);
  }

  private isImagePreview(fileExtension?: string | null, fileMimeType?: string | null): boolean {
    const normalizedExtension = this.normalizeExtension(fileExtension);
    const normalizedMimeType = (fileMimeType || '').toLowerCase();

    return (
      (normalizedMimeType.startsWith('image/') && normalizedMimeType !== 'image/svg+xml') ||
      ['.jpeg', '.jpg', '.png', '.bmp'].includes(normalizedExtension)
    );
  }

  private isPdfPreview(fileExtension?: string | null, fileMimeType?: string | null): boolean {
    const normalizedExtension = this.normalizeExtension(fileExtension);
    const normalizedMimeType = (fileMimeType || '').toLowerCase();

    if (normalizedExtension === '.pdf') {
      return true;
    }

    return normalizedMimeType === 'application/pdf';
  }

  private normalizeGetFileOptions(options?: boolean | GetFileOptions): Required<GetFileOptions> {
    if (typeof options === 'boolean') {
      return {
        forceDownload: options,
        tryOpenOnMobile: false,
        previewTargetKey: '',
        allowDownloadFallback: false,
      };
    }

    return {
      forceDownload: options?.forceDownload ?? false,
      tryOpenOnMobile: options?.tryOpenOnMobile ?? false,
      previewTargetKey: options?.previewTargetKey ?? '',
      allowDownloadFallback: options?.allowDownloadFallback ?? false,
    };
  }

  private buildPreparedPreviewTargetKey(key: string): string {
    this.previewTargetCounter += 1;

    return `${key}::${Date.now()}::${this.previewTargetCounter}`;
  }

  private getFileName(file: HttpResponse<Blob>): string {
    const encodedFileName = file.headers.get('File-Name');

    if (!encodedFileName) {
      return 'download';
    }

    try {
      return decodeURIComponent(encodedFileName);
    } catch {
      return encodedFileName;
    }
  }

  private normalizeExtension(fileExtension?: string | null): string {
    if (!fileExtension) {
      return '';
    }

    const normalizedExtension = fileExtension.toString().trim().toLowerCase();

    return normalizedExtension.startsWith('.') ? normalizedExtension : `.${normalizedExtension}`;
  }

  private consumePreparedPreviewTarget(key: string): Window | null {
    const previewWindow = this.preparedPreviewTargets.get(key) ?? null;
    this.preparedPreviewTargets.delete(key);
    return previewWindow;
  }

  private openFileInTab(opts: {
    fileBody: Blob;
    fileMimeType: string | null;
    fileName: string;
    previewWindow: Window | null;
    useDirectNavigation: boolean;
  }): boolean {
    const objectUrl = URL.createObjectURL(opts.fileBody);

    try {
      if (opts.previewWindow) {
        if (opts.useDirectNavigation) {
          opts.previewWindow.location.replace(objectUrl);
        } else {
          this.renderFilePreview(opts.previewWindow, objectUrl, opts.fileName, opts.fileMimeType);
        }

        return true;
      }

      if (opts.useDirectNavigation) {
        const previewWindow = window.open(objectUrl, '_blank');

        if (!previewWindow) {
          URL.revokeObjectURL(objectUrl);
          return false;
        }

        return true;
      }

      const previewWindow = window.open('', '_blank');

      if (!previewWindow) {
        URL.revokeObjectURL(objectUrl);
        return false;
      }

      this.renderFilePreview(previewWindow, objectUrl, opts.fileName, opts.fileMimeType);
      return true;
    } catch {
      URL.revokeObjectURL(objectUrl);
      this.closePreviewWindow(opts.previewWindow);
      return false;
    }
  }

  private shouldUseDirectNavigationPreview(fileExtension?: string | null, fileMimeType?: string | null): boolean {
    const deviceInfo = this.deviceInfoService.getInfo();
    const userAgent = window.navigator.userAgent;
    const isImagePreview = this.isImagePreview(fileExtension, fileMimeType);

    if (this.isPdfPreview(fileExtension, fileMimeType)) {
      return true;
    }

    if (deviceInfo.deviceTypeDetected !== 'DESKTOP') {
      return !isImagePreview;
    }

    if (isImagePreview) {
      return false;
    }

    return isSafariBrowser(deviceInfo.deviceInfo.browser, userAgent) || isEmbeddedBrowser(userAgent);
  }

  private renderLoadingPreview(previewWindow: Window): void {
    previewWindow.document.open();
    previewWindow.document.write(FILE_PREVIEW_LOADING_HTML);
    previewWindow.document.close();
    previewWindow.document.title = FILE_PREVIEW_LOADING_TITLE;
  }

  private renderFilePreview(
    previewWindow: Window,
    objectUrl: string,
    fileName: string,
    fileMimeType: string | null
  ): void {
    const useMobileImagePreview = this.deviceInfoService.getInfo().deviceTypeDetected !== 'DESKTOP';

    previewWindow.addEventListener('beforeunload', () => URL.revokeObjectURL(objectUrl), { once: true });

    this.nativeFileHandlerService.render(previewWindow, objectUrl, fileName, fileMimeType, useMobileImagePreview);
  }

  private downloadFile(fileBody: Blob, fileName: string): void {
    const url = window.URL.createObjectURL(fileBody);
    const anchor = document.createElement('a');

    anchor.download = fileName;
    anchor.href = url;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    window.setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  }

  private closePreviewWindow(previewWindow: Window | null): void {
    if (previewWindow && !previewWindow.closed) {
      previewWindow.close();
    }
  }

  private showUnsupportedTypeError(): void {
    this.notification.showError({
      message: 'COMMON.ERROR.UNSUPPORTED_TYPE_OF_FILE_CONTACT_ADMIN',
      duration: 3000,
    });
  }
}
